import { AxiosError } from 'axios';
import { MatrixClient } from 'matrix-js-sdk/src/matrix';
import { createContext, PropsWithChildren, useCallback, useMemo, useRef, useState } from 'react';

import { listScheduledMeetings, MeetingType } from 'api/meedioBackend/listScheduledMeetings';
import {
  chunkRoomIds,
  mergeMeetingsForMessengerRoom,
  preserveRoomMeetingsInBulk,
} from 'api/meedioBackend/schedulingMeetingsList';
import { MeetingResponse } from 'api/meedioBackend/types';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { getAllMessengerRooms } from 'modules/Messenger/components/ConversationsList/utils';
import logger from 'utils/logging/faro';

export interface MeetingsScheduleContextValue {
  meetings: MeetingResponse[];
  isLoading: boolean;
  error: unknown;
  refetchAllMessengerRooms: () => Promise<void>;
  refetchMessengerRoom: (roomId: string) => Promise<void>;
  getMeetingsForMessengerRoom: (roomId: string) => MeetingResponse[];
}

export const MeetingsScheduleContext = createContext<MeetingsScheduleContextValue | null>(null);

const isUnauthorizedError = (error: unknown): boolean => error instanceof AxiosError && error.response?.status === 401;

// Per-room refetch that completes just before a bulk replace (e.g. chat join hydration)
// is not covered by AC-B18 (in-flight only). Reconcile those rooms after bulk so a stale
// bulk payload cannot flash-remove meetings that a fresh single-room fetch already loaded.
const RECENT_PER_ROOM_REFETCH_RECONCILE_MS = 15_000;

const getRecentlyRefetchedRoomIds = (recentPerRoomRefetchAt: Map<string, number>, now = Date.now()): string[] =>
  [...recentPerRoomRefetchAt.entries()]
    .filter(([, refetchedAt]) => now - refetchedAt <= RECENT_PER_ROOM_REFETCH_RECONCILE_MS)
    .map(([roomId]) => roomId);

const getBulkDroppedRecentlyRefetchedRooms = (
  prevMeetings: MeetingResponse[],
  bulkMeetings: MeetingResponse[],
  recentRoomIds: string[]
): string[] =>
  recentRoomIds.filter((roomId) => {
    const hadInPrev = prevMeetings.some((meeting) => meeting.messenger_room_id === roomId);
    const hasInBulk = bulkMeetings.some((meeting) => meeting.messenger_room_id === roomId);
    return hadInPrev && !hasInBulk;
  });

// Full bulk fetch: batch all messenger room ids into ≤50-id chunks and fetch
// scheduled meetings for each. Sequential so a failing chunk fails the whole
// bulk (AC partial-batch rule: no partial replace).
const fetchBulkScheduledMeetings = async (matrixClient: MatrixClient): Promise<MeetingResponse[]> => {
  const roomIds = getAllMessengerRooms(matrixClient).map((room) => room.roomId);
  const chunks = chunkRoomIds(roomIds);
  if (chunks.length === 0) return [];

  const results: MeetingResponse[] = [];
  for (const messengerRoomIds of chunks) {
    const chunkMeetings = await listScheduledMeetings({
      messengerRoomIds,
      type: MeetingType.Scheduled,
    });
    results.push(...chunkMeetings);
  }
  return results;
};

const MeetingsScheduleProvider = ({ children }: PropsWithChildren) => {
  const { matrixClient } = useMatrixContext({ requireDefinedMatrixClient: false });
  const [meetings, setMeetings] = useState<MeetingResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // AC-B18: a per-room refetch can land newer data while a full bulk is still
  // in flight. Record those rooms so we can re-merge them after the bulk
  // replace, so the bulk's older payload never permanently drops a fresh row.
  const isBulkInFlightRef = useRef(false);
  const roomsToReconcileAfterBulkRef = useRef(new Set<string>());
  const recentPerRoomRefetchAtRef = useRef(new Map<string, number>());
  const meetingsSnapshotRef = useRef<MeetingResponse[]>([]);

  const refetchMessengerRoom = useCallback(
    async (roomId: string) => {
      if (!matrixClient) return;

      setIsLoading(true);
      setError(null);
      try {
        const freshForRoom = await listScheduledMeetings({
          messengerRoomId: roomId,
          type: MeetingType.Scheduled,
        });
        if (isBulkInFlightRef.current) roomsToReconcileAfterBulkRef.current.add(roomId);
        recentPerRoomRefetchAtRef.current.set(roomId, Date.now());
        setMeetings((prev) => {
          const next = mergeMeetingsForMessengerRoom(prev, roomId, freshForRoom);
          meetingsSnapshotRef.current = next;
          return next;
        });
      } catch (err) {
        logger.warn('MeetingsScheduleProvider: refetchMessengerRoom failed', { error: err, roomId });
        setError(err);
        // Per-room failure keeps prior rows (no flash-empty); 401 clears the store.
        if (isUnauthorizedError(err)) {
          meetingsSnapshotRef.current = [];
          setMeetings([]);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [matrixClient]
  );

  const refetchAllMessengerRooms = useCallback(async () => {
    if (!matrixClient) return;

    isBulkInFlightRef.current = true;
    roomsToReconcileAfterBulkRef.current.clear();
    setIsLoading(true);
    setError(null);
    try {
      const bulkMeetings = await fetchBulkScheduledMeetings(matrixClient);
      // Snapshot after bulk fetch so in-flight per-room merges during the request are included.
      const prevMeetings = meetingsSnapshotRef.current;

      const recentlyRefetchedRoomIds = getRecentlyRefetchedRoomIds(recentPerRoomRefetchAtRef.current);
      const inFlightPerRoomRooms = [...roomsToReconcileAfterBulkRef.current];
      const bulkDroppedRecentRooms = getBulkDroppedRecentlyRefetchedRooms(
        prevMeetings,
        bulkMeetings,
        recentlyRefetchedRoomIds
      );

      const roomsToPreserve = [...new Set([...inFlightPerRoomRooms, ...bulkDroppedRecentRooms])];
      const mergedMeetings = preserveRoomMeetingsInBulk(
        prevMeetings,
        bulkMeetings,
        roomsToPreserve,
        inFlightPerRoomRooms
      );

      setMeetings(mergedMeetings);
      meetingsSnapshotRef.current = mergedMeetings;
      isBulkInFlightRef.current = false;
      roomsToReconcileAfterBulkRef.current.clear();
    } catch (err) {
      isBulkInFlightRef.current = false;
      roomsToReconcileAfterBulkRef.current.clear();
      logger.warn('MeetingsScheduleProvider: refetchAllMessengerRooms failed', { error: err });
      setError(err);
      // Bulk failure keeps last good store (no partial replace); 401 clears it.
      if (isUnauthorizedError(err)) {
        meetingsSnapshotRef.current = [];
        setMeetings([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [matrixClient]);

  const getMeetingsForMessengerRoom = useCallback(
    (roomId: string) => meetings.filter((meeting) => meeting.messenger_room_id === roomId),
    [meetings]
  );

  const value = useMemo(
    () => ({
      meetings,
      isLoading,
      error,
      refetchAllMessengerRooms,
      refetchMessengerRoom,
      getMeetingsForMessengerRoom,
    }),
    [meetings, isLoading, error, refetchAllMessengerRooms, refetchMessengerRoom, getMeetingsForMessengerRoom]
  );

  return <MeetingsScheduleContext.Provider value={value}>{children}</MeetingsScheduleContext.Provider>;
};

export default MeetingsScheduleProvider;
